import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { Confetti } from "./Confetti";
import type { Topic, Vote } from "@offsite/shared";

export function ParticipantTeamView() {
  const { state, dispatch } = useStore();
  // Solo-Offsites (AR) haben kein Voting: `/votes` antwortet dort mit 403,
  // und Konfetti gehört nicht in eine Gremiensitzung.
  const { soloTeams } = useOffsiteConfig();
  const [vote, setVote] = useState<Vote | null>(null);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [showedConfetti, setShowedConfetti] = useState(false);

  // Poll /vote/me — we need the team number once it's assigned.
  useEffect(() => {
    if (soloTeams) return; // kein Voting → kein /votes, sonst 403 im Sekundentakt
    let cancelled = false;
    const load = async () => {
      try {
        const [v, t] = await Promise.all([
          api.votes.me(state.auth.token),
          api.topics.list(state.auth.token),
        ]);
        if (cancelled) return;
        setVote(v.vote);
        setTopics(t.topics);
        dispatch({ type: "topics/set", topics: t.topics });
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [state.auth.token, dispatch, soloTeams]);

  const winnerTopic = topics[0]; // best-effort fallback; real winner comes via vote.topicId? actually no — vote.topicId is THIS user's vote, not the winner. We don't know the winner client-side. Just show team number.
  void winnerTopic;

  // Trigger confetti once when the team number first becomes known.
  useEffect(() => {
    if (vote?.teamNumber && !showedConfetti) {
      setShowedConfetti(true);
    }
  }, [vote?.teamNumber, showedConfetti]);

  if (!vote) {
    return (
      <section style={{ padding: "2rem 0", textAlign: "center", display: "grid", gap: 12 }}>
        <div className="deck-eyebrow">Warten</div>
        <p className="deck-sub" style={{ margin: 0 }}>
          Du hast nicht abgestimmt — die Teams werden nur unter den Wählern verteilt.
        </p>
      </section>
    );
  }

  if (!vote.teamNumber) {
    return (
      <section style={{ padding: "2rem 0", textAlign: "center", display: "grid", gap: 12 }}>
        <div className="deck-eyebrow">Gleich geht's los</div>
        <p className="deck-sub" style={{ margin: 0 }}>
          Der Moderator verteilt gerade die Teams …
        </p>
      </section>
    );
  }

  const isCaptain = vote.isCaptain === true;

  return (
    <section className="part-team" style={{ display: "grid", gap: 16, padding: "1rem 0", textAlign: "center" }}>
      {showedConfetti && !soloTeams && <Confetti count={50} />}
      <div className="deck-eyebrow">Dein Team steht</div>
      <div className="part-team-num">
        <span className="part-team-num-label">Team</span>
        <span className="part-team-num-value">{vote.teamNumber}</span>
      </div>
      {isCaptain && (
        <div className="part-team-captain-badge">
          ⭐ Du bist Schriftführer
        </div>
      )}
      <p className="deck-sub" style={{ margin: 0 }}>
        {isCaptain
          ? "Sobald die Arbeitsphase startet, gibst du den Teamnamen ein und schreibst die Anforderung."
          : "Findet euch zusammen — gleich geht's mit der Anforderung los."}
      </p>
    </section>
  );
}
